/**
 * The ageing report's rows (`report_ageing`, 0036), as every ageing screen
 * reads them — Today's glance and Reports alike (docs/tally/18 §1.3, D3):
 * a sheet's own four bands, or a TallyPrime company's own windows with what
 * they leave beside them, each named exactly as Settings names it.
 */
import { AGEING_BANDS, bucketLabels, type AgeingBasis } from '@timely/domain';

export type AgeingRowKind = 'band' | 'window' | 'not_yet_due' | 'not_aged' | 'credits';

export interface AgeingReportRow {
    bucket: string;
    /** The Book filter that opens exactly the accounts this row counted. */
    filterValue: string;
    amount: number;
    accountCount: number;
    kind: AgeingRowKind;
    /** On a window row: the company's basis and bounds. */
    layout: { basis: AgeingBasis; boundaries: number[] } | null;
}

const KINDS: readonly AgeingRowKind[] = ['band', 'window', 'not_yet_due', 'not_aged', 'credits'];
const num = (v: unknown): number => Number(v ?? 0) || 0;

/** The database's rows, read defensively; an older database without the new columns gives bands. */
export function readAgeingReport(rows: readonly Record<string, unknown>[]): AgeingReportRow[] {
    return rows.map(row => {
        const kind = KINDS.includes(row['kind'] as AgeingRowKind) ? row['kind'] as AgeingRowKind : 'band';
        const l = row['layout'] as { basis?: unknown; boundaries?: unknown } | null | undefined;
        const layout = l && (l.basis === 'bill_date' || l.basis === 'due_date') && Array.isArray(l.boundaries) && l.boundaries.every(b => Number.isInteger(b))
            ? { basis: l.basis as AgeingBasis, boundaries: l.boundaries as number[] } : null;
        return { bucket: String(row['bucket']), filterValue: String(row['filter_value']), amount: num(row['amount']), accountCount: num(row['account_count']), kind, layout };
    });
}

/** The rows an age bar draws: a sheet's bands, or the company's windows. */
export const agedRows = (rows: readonly AgeingReportRow[]): AgeingReportRow[] => rows.filter(r => r.kind === 'band' || r.kind === 'window');

/** What the windows leave beside them, in the report's order: not yet due, in no bucket, credits. */
export const besideRows = (rows: readonly AgeingReportRow[]): AgeingReportRow[] => rows.filter(r => r.kind !== 'band' && r.kind !== 'window');

/** A row's name, as Settings names it. */
export function ageingRowLabel(row: AgeingReportRow, rows: readonly AgeingReportRow[]): string {
    switch (row.kind) {
        case 'band': return AGEING_BANDS.find(b => b.bucket === row.bucket)?.label ?? row.bucket;
        case 'window': {
            const layout = row.layout ?? rows.find(r => r.layout)?.layout ?? null;
            const index = Number(row.bucket.replace(/^w/, '')) - 1;
            return (layout && bucketLabels(layout)[index]) || row.bucket;
        }
        case 'not_yet_due': return 'Not yet due';
        case 'not_aged': return 'Not in any bucket';
        case 'credits': return 'Credits, against no bill yet';
    }
}
